import {
  Component,
  DestroyRef,
  ViewChild,
  afterNextRender,
  inject,
  signal,
} from '@angular/core';
import { AuthService } from '../../../../core/services/auth.service';
import { prefersReducedMotion } from '../../../../core/utils/motion.util';
import {
  SUGGESTED_GOALS,
  SuggestedGoal,
  TYPEWRITER_TAGLINES,
} from '../../data/landing-content';
import { PathCardStageComponent } from '../path-card-stage/path-card-stage.component';
import { PromptBoxComponent } from '../prompt-box/prompt-box.component';

interface PromptSubmission {
  prompt: string;
  timestamp: string;
}

const TYPEWRITER = {
  typeMs: 60,
  deleteMs: 30,
  pauseAfterMs: 1800,
  pauseBeforeMs: 300,
} as const;

@Component({
  selector: 'app-hero',
  standalone: true,
  imports: [PromptBoxComponent, PathCardStageComponent],
  template: `
    <section class="hero" id="hero">
      <div class="hero-inner">
        <div class="hero-left">
          <p class="hero-eyebrow">
            <span class="kanji-inline">改善</span> kaizen — continuous
            improvement
          </p>

          <h1 class="hero-headline">
            <span class="l"><span>Mastery isn't a moment.</span></span>
            <span class="l"
              ><span>It's a <span class="accent">practice.</span></span></span
            >
          </h1>

          <div class="typewriter-row">
            <span class="typewriter-text">{{ typewriterText() }}</span>
            <span class="cursor"></span>
          </div>

          <app-prompt-box
            [value]="promptValue()"
            (valueChange)="promptValue.set($event)"
            (submit)="handleSubmit()"
          />

          <div class="hint-chips">
            @for (goal of suggestedGoals; track goal.label) {
              <button
                class="hint-chip"
                [class.active]="activeGoal() === goal.label"
                (click)="selectGoal(goal)"
              >
                {{ goal.label }}
              </button>
            }
          </div>
        </div>

        <div class="hero-right">
          <app-path-card-stage #pathStage />
        </div>
      </div>
    </section>
  `,
  styleUrl: './hero.component.scss',
})
export class HeroComponent {
  @ViewChild('pathStage') private pathStage?: PathCardStageComponent;

  readonly suggestedGoals = SUGGESTED_GOALS;
  readonly typewriterText = signal('');
  readonly promptValue = signal('');
  readonly activeGoal = signal<string | null>(null);
  readonly isSubmitting = signal(false);

  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  private lineIndex = 0;
  private charIndex = 0;
  private isDeleting = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    afterNextRender(() => {
      if (prefersReducedMotion()) {
        this.typewriterText.set(TYPEWRITER_TAGLINES[0]);
      } else {
        this.tick();
        this.destroyRef.onDestroy(() => {
          if (this.timer) clearTimeout(this.timer);
        });
      }
    });
  }

  selectGoal(goal: SuggestedGoal): void {
    this.promptValue.set(goal.prompt);
    this.activeGoal.set(goal.label);
    this.pathStage?.swapTo(goal.pathIndex, 0);
  }

  async handleSubmit(): Promise<void> {
    const prompt = this.promptValue().trim();
    if (!prompt || this.isSubmitting()) return;

    this.isSubmitting.set(true);

    const submission: PromptSubmission = {
      prompt,
      timestamp: new Date().toISOString(),
    };

    try {
      await this.authService.signup(submission);
    } catch {
      this.isSubmitting.set(false);
    }
  }

  private tick(): void {
    const line = TYPEWRITER_TAGLINES[this.lineIndex];

    if (this.isDeleting) {
      this.charIndex--;
      this.typewriterText.set(line.slice(0, this.charIndex));

      if (this.charIndex === 0) {
        this.isDeleting = false;
        this.lineIndex = (this.lineIndex + 1) % TYPEWRITER_TAGLINES.length;
        this.timer = setTimeout(() => this.tick(), TYPEWRITER.pauseBeforeMs);
        return;
      }

      this.timer = setTimeout(() => this.tick(), TYPEWRITER.deleteMs);
    } else {
      this.charIndex++;
      this.typewriterText.set(line.slice(0, this.charIndex));

      if (this.charIndex === line.length) {
        this.isDeleting = true;
        this.timer = setTimeout(() => this.tick(), TYPEWRITER.pauseAfterMs);
        return;
      }

      this.timer = setTimeout(() => this.tick(), TYPEWRITER.typeMs);
    }
  }
}
